class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.defaultSearch = this.querySelector('.default--search');
    this.demoSearch = this.querySelector('.wbsclose');
    this.searchActiveClass = this.dataset.searchActiveClass;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    if (form) {
      form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    if (this.demoSearch) {
      this.demoSearch.addEventListener('click', this.onClick.bind(this));
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  buildSearchUrl(searchTerm) {
    return `/search?q=${encodeURIComponent(searchTerm)}&type=product&options[prefix]=last&options[fields]=title,product_type,variants.title,tag`;
  }

  buildSearchParams(searchTerm) {
    const params = new URLSearchParams({
      q: searchTerm,
      section_id: 'predictive-search',
      'resources[type]': 'product,page,article,collection',
      'resources[limit]': '10',
      // 20-08-2026: DE BELANGRIJKSTE REGEL HIERONDER.
      // Zonder 'resources[limit_scope]' hanteert Shopify scope 'all': de limiet
      // van 10 geldt dan over ALLE resourcetypes SAMEN. Voedingswaarden-pagina's,
      // blogartikelen en collecties aten daardoor de plekken op die voor producten
      // bedoeld waren. Zoeken op "snoep" gaf 3 producten + 3 pagina's + 1 artikel
      // + 3 collecties = 10; zoeken op "eiwitpoeder" gaf 4 producten + 4 pagina's
      // + 1 artikel + 1 collectie = 10. Terwijl er 28 resp. 10+ producten zijn.
      // Met scope 'each' krijgt elk type zijn eigen 10; het aantal dat werkelijk
      // getoond wordt, wordt in sections/predictive-search.liquid begrensd.
      'resources[limit_scope]': 'each',
      // 19-08-2026: was 'show', waardoor uitverkochte producten gewoon op hun
      // relevantieplek bleven staan — vandaar dat een zoekopdracht op "reep" de
      // uitverkochte Goodgood-reep als eerste resultaat gaf. Met 'last' zet
      // Shopify ze zelf achteraan, en dat gebeurt server-side over ALLE treffers
      // en niet alleen over de tien die hier terugkomen. Bij genoeg leverbare
      // alternatieven vallen ze dus helemaal uit de lijst, en zoek je het
      // product bij naam dan staat het er nog gewoon. Waarden: show | hide | last.
      // Bewust niet 'hide': wie op "speculoos koekjes" zoekt en niets vindt,
      // concludeert dat je het niet meer verkoopt.
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,tag'
    });

    return params.toString();
  }

  onChange() {
    super.onChange();
    const searchTerm = this.getQuery();
    const newSearchTerm = this.getQuery();

    if (this.defaultSearch && !this.defaultSearch.classList.contains('hidden')) {
      this.defaultSearch.classList.add('hidden');
    }

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('.search_content #predictive-search-results-groups-wrapper')?.remove();
    }

    if (!searchTerm.length) {
      this.searchTerm = '';
      if (this.defaultSearch) this.defaultSearch.classList.remove('hidden');
      this.hiddenClassActivity(true);
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    const query = this.getQuery();
    const selectedLink = this.querySelector('[aria-selected="true"] a');

    if (!query.length) {
      event.preventDefault();
      return;
    }

    if (selectedLink) {
      event.preventDefault();
      selectedLink.click();
      return;
    }

    event.preventDefault();
    window.location.assign(this.buildSearchUrl(query));
  }

  onFormReset(event) {
    super.onFormReset(event);

    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      this.predictiveSearchResults.classList.remove('hidden');
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      if (this.defaultSearch) this.defaultSearch.classList.remove('hidden');
      if (this.demoSearch) this.demoSearch.classList.remove('hidden');
      this.hiddenClassActivity(true);
      return;
    }

    if (this.getAttribute('results') === 'true') {
      this.hiddenClassActivity(true);
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        if (this.defaultSearch) this.defaultSearch.classList.add('hidden');
        if (this.demoSearch) this.demoSearch.classList.add('hidden');
        this.hiddenClassActivity(false);
        this.close();
      }
    }, 100);
  }

  onClick() {
    setTimeout(() => {
      if (this.defaultSearch) this.defaultSearch.classList.add('hidden');
      if (this.demoSearch) this.demoSearch.classList.add('hidden');
      this.hiddenClassActivity(false);
      this.close();
    });
  }

  onKeyup(event) {
    const query = this.getQuery();

    if (!query.length) {
      this.close(true);
      return;
    }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter': {
        const selectedLink = this.querySelector('[aria-selected="true"] a');
        event.preventDefault();

        if (selectedLink) {
          selectedLink.click();
        } else {
          window.location.assign(this.buildSearchUrl(query));
        }
        break;
      }
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
    if (selectedProduct) {
      selectedProduct.click();
    } else if (this.getQuery().length) {
      window.location.assign(this.buildSearchUrl(this.getQuery()));
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.searchTerm = searchTerm;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();

    fetch(
      `/search/suggest?${this.buildSearchParams(searchTerm)}`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.hiddenClassActivity(false);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');

        const resultsMarkup = section ? section.innerHTML : '';

        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) return;
        this.hiddenClassActivity(false);
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.sanitizeLinks();
    this.setLiveRegionResults();
    this.hiddenClassActivity(true);
    this.open();
  }

  sanitizeLinks() {
    try {
      const links = this.predictiveSearchResults.querySelectorAll('a');

      links.forEach((a) => {
        try {
          const url = new URL(a.href, window.location.origin);

          if (url.pathname.indexOf('/products/') === 0) {
            url.search = '';
            a.href = url.toString();
          }

          if (url.pathname === '/search') {
            const q = url.searchParams.get('q') || '';
            a.href = this.buildSearchUrl(q);
          }
        } catch (e) {}
      });
    } catch (e) {}
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveRegion = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegion) {
      this.setLiveRegionText(liveRegion.textContent);
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.searchTerm = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }

  hiddenClassActivity(isAddOpration = false) {
    if (!this.searchActiveClass) return;

    if (!document.body.classList.contains(this.searchActiveClass) && isAddOpration) {
      document.body.classList.add(this.searchActiveClass);
    } else if (document.body.classList.contains(this.searchActiveClass) && !isAddOpration) {
      document.body.classList.remove(this.searchActiveClass);
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
